import { useState, useEffect } from "react";
import { jsPDF } from "jspdf";

const Q_PDF = { azul:[28,63,170], azulVivo:[42,127,255] };

// ALQOS es el nombre del software, no el de la empresa del tenant que lo
// usa -- el membrete tiene que mostrar el nombre real de la empresa
// (usuarios.empresa), cacheado en localStorage al loguear (GestorUsuarios.js)
// porque este componente no recibe `usuario` como prop y se llama desde 33
// módulos distintos. Si no hay empresa configurada (o el tenant nunca la
// cambió del valor demo "ALQOS™"), cae al mismo fallback genérico que ya
// usan Cotizacion.js/BalanceSimplificado.js en esta situación.
function membreteEmpresa() {
  try {
    const empresa = localStorage.getItem("alqos_empresa") || "";
    if (empresa && empresa !== "ALQOS™") {
      return { principal: empresa, sub: "Instructivo de Uso · Gestionado con ALQOS™" };
    }
  } catch {}
  return { principal: "ALQOS™", sub: "Sistema de Gestión Integral · Instructivo de Uso" };
}
// Logo opcional del tenant (cacheado en base64 al loguear, ver
// cachearLogoEmpresa en GestorUsuarios.js). Si no hay logo cargado, el
// membrete sigue funcionando solo con texto -- no bloqueante.
function logoEmpresa() {
  try {
    const dataUrl = localStorage.getItem("alqos_logo_empresa");
    const ratio = parseFloat(localStorage.getItem("alqos_logo_ratio"));
    if (dataUrl && ratio > 0) {
      const formato = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
      return { dataUrl, ratio, formato };
    }
  } catch {}
  return null;
}

// Genera el PDF del instructivo con membrete de la empresa del tenant --
// exportada para que tanto este botón (vista publicada) como el editor de
// Instructivos.js (previsualizar un borrador antes de aprobarlo) usen la
// misma función, en vez de un archivo nuevo suelto o duplicar la lógica.
export function generarPdfInstructivo(instructivo) {
  const doc = new jsPDF({ unit:"mm", format:"a4" });
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const m = 18;

  const dibujarEncabezado = () => {
    const { principal, sub } = membreteEmpresa();
    doc.setFillColor(...Q_PDF.azul);
    doc.rect(0, 0, w, 26, "F");
    doc.setFont(undefined, "bold"); doc.setFontSize(16); doc.setTextColor(255,255,255);
    doc.text(principal, m, 15);
    doc.setFont(undefined, "normal"); doc.setFontSize(9); doc.setTextColor(220,228,255);
    doc.text(sub, m, 21);
    // Logo a la derecha del header, alto fijo y ancho según su relación de
    // aspecto real (capado además en ancho máximo, por si el logo es muy
    // panorámico) -- para que no rompa el layout con proporciones raras.
    const logo = logoEmpresa();
    if (logo) {
      const altoLogo = 16;
      const anchoLogo = Math.min(altoLogo * logo.ratio, 40);
      try { doc.addImage(logo.dataUrl, logo.formato, w - m - anchoLogo, 5, anchoLogo, altoLogo); } catch {}
    }
  };
  dibujarEncabezado();

  let y = 38;
  doc.setFont(undefined, "bold"); doc.setFontSize(15); doc.setTextColor(...Q_PDF.azul);
  const tituloLineas = doc.splitTextToSize(instructivo.titulo || "Instructivo", w - 2*m);
  doc.text(tituloLineas, m, y);
  y += tituloLineas.length * 6 + 2;
  doc.setFont(undefined, "normal"); doc.setFontSize(9); doc.setTextColor(120,120,120);
  doc.text(`Versión ${instructivo.version || "1.0"}${instructivo.estado === "borrador" ? " · BORRADOR -- pendiente de aprobación" : ""}`, m, y);
  y += 8;

  const firmas = [["Elaborado por", instructivo.elaboradoPor], ["Revisado por", instructivo.revisadoPor], ["Aprobado por", instructivo.aprobadoPor]].filter(([,v]) => v);
  if (firmas.length) {
    const colW = (w - 2*m) / firmas.length;
    firmas.forEach(([label, valor], i) => {
      const x = m + i*colW;
      doc.setDrawColor(...Q_PDF.azulVivo); doc.setLineWidth(0.3);
      doc.rect(x, y, colW - 4, 14);
      doc.setFont(undefined, "bold"); doc.setFontSize(7.5); doc.setTextColor(...Q_PDF.azulVivo);
      doc.text(label.toUpperCase(), x+3, y+5);
      doc.setFont(undefined, "normal"); doc.setFontSize(9); doc.setTextColor(40,40,40);
      doc.text(String(valor), x+3, y+10);
    });
    y += 20;
  }

  doc.setDrawColor(220,220,220); doc.setLineWidth(0.2);
  doc.line(m, y, w-m, y);
  y += 8;

  doc.setFont(undefined, "normal"); doc.setFontSize(10.5); doc.setTextColor(40,40,40);
  const lineas = doc.splitTextToSize(instructivo.contenido_texto || "", w - 2*m);
  const lineHeight = 5.2;
  const yFinPagina = h - 16;
  lineas.forEach(linea => {
    if (y > yFinPagina) { doc.addPage(); dibujarEncabezado(); y = 38; }
    doc.text(linea, m, y);
    y += lineHeight;
  });

  const totalPaginas = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPaginas; p++) {
    doc.setPage(p);
    doc.setFont(undefined, "normal"); doc.setFontSize(7.5); doc.setTextColor(150,150,150);
    doc.text(`ALQOS™ · SISTEMA DE GESTIÓN INTEGRAL · by QORVEX · CONFIDENCIAL · © ${new Date().getFullYear()}`, w/2, h-8, { align:"center" });
    doc.text(`${p}/${totalPaginas}`, w-m, h-8, { align:"right" });
  }
  return doc;
}
function descargarPdfInstructivo(instructivo) {
  const doc = generarPdfInstructivo(instructivo);
  const nombreArchivo = `Instructivo_${(instructivo.modulo_key||"alqos")}_v${instructivo.version||"1.0"}.pdf`.replace(/\s+/g,"_");
  doc.save(nombreArchivo);
}

export default function BotonInstructivo({ moduloKey, oscuro }) {
  const [instructivo, setInstructivo] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [vistaSimple, setVistaSimple] = useState(false);

  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('instructivos').select('data');
        if (data && data.length > 0) {
          const todos = data.map(r => r.data);
          const found = todos.find(i => i.modulo_key === moduloKey && i.estado === "publicado");
          setInstructivo(found || null);
        } else {
          const local = JSON.parse(localStorage.getItem('alqos_instructivos') || '[]');
          setInstructivo(local.find(i => i.modulo_key === moduloKey && i.estado === "publicado") || null);
        }
      } catch {
        const local = JSON.parse(localStorage.getItem('alqos_instructivos') || '[]');
        setInstructivo(local.find(i => i.modulo_key === moduloKey && i.estado === "publicado") || null);
      }
    }
    cargar();
  }, [moduloKey]);

  if (!instructivo) return null;

  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const subtexto = oscuro ? "#9aabd4" : "#808085";

  return (
    <>
      <button
        onClick={() => setAbierto(true)}
        title="Ver instructivo de este módulo"
        style={{
          background: "rgba(123,91,242,0.12)",
          border: "1px solid rgba(123,91,242,0.3)",
          color: "#7B5BF2",
          borderRadius: "8px",
          padding: "5px 10px",
          fontSize: "13px",
          cursor: "pointer",
          fontWeight: "600",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px"
        }}>
        ❓ Instructivo
      </button>

      {abierto && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
          padding: "1rem"
        }}
          onClick={e => { if (e.target === e.currentTarget) setAbierto(false); }}>
          <div style={{
            background: cardBg, borderRadius: "16px", padding: "1.5rem",
            maxWidth: "600px", width: "100%", maxHeight: "80vh",
            overflowY: "auto", border: `1px solid ${borde}`,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
              <div>
                <p style={{ fontSize: "11px", color: "#7B5BF2", fontWeight: "600", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: ".1em" }}>📖 Instructivo</p>
                <p style={{ fontSize: "16px", fontWeight: "700", color: texto, margin: 0 }}>{instructivo.titulo}</p>
                <p style={{ fontSize: "11px", color: subtexto, margin: "3px 0 0" }}>v{instructivo.version}</p>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <button onClick={() => descargarPdfInstructivo(instructivo)} title="Descargar instructivo en PDF"
                  style={{ background: "rgba(28,63,170,0.1)", border: "1px solid rgba(28,63,170,0.3)", color: "#1C3FAA", borderRadius: "8px", padding: "5px 10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
                  📄 Descargar PDF
                </button>
                <button onClick={() => setAbierto(false)}
                  style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: subtexto }}>✕</button>
              </div>
            </div>

            {instructivo.contenido_simple && (
              <div style={{ display: "flex", gap: "6px", marginBottom: "1rem" }}>
                <button onClick={() => setVistaSimple(false)}
                  style={{ padding: "5px 12px", borderRadius: "8px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: !vistaSimple ? "2px solid #7B5BF2" : `1px solid ${borde}`, background: !vistaSimple ? "rgba(123,91,242,.1)" : cardBg, color: !vistaSimple ? "#7B5BF2" : subtexto }}>
                  Técnico
                </button>
                <button onClick={() => setVistaSimple(true)}
                  style={{ padding: "5px 12px", borderRadius: "8px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: vistaSimple ? "2px solid #22c55e" : `1px solid ${borde}`, background: vistaSimple ? "rgba(34,197,94,.1)" : cardBg, color: vistaSimple ? "#22c55e" : subtexto }}>
                  👷 Para operarios
                </button>
              </div>
            )}

            {(instructivo.elaboradoPor || instructivo.revisadoPor || instructivo.aprobadoPor) && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px", marginBottom: "1rem" }}>
                {[["✍️ Elaborado", instructivo.elaboradoPor], ["👁️ Revisado", instructivo.revisadoPor], ["✅ Aprobado", instructivo.aprobadoPor]].map(([label, valor]) => valor && (
                  <div key={label} style={{ background: oscuro ? "#091840" : "#f5f3ff", border: "1px solid #7B5BF240", borderRadius: "8px", padding: "8px 10px" }}>
                    <p style={{ fontSize: "10px", color: "#7B5BF2", fontWeight: "600", margin: "0 0 2px" }}>{label}</p>
                    <p style={{ fontSize: "12px", color: texto, margin: 0, fontWeight: "500" }}>{valor}</p>
                  </div>
                ))}
              </div>
            )}
            <div style={{
              fontSize: "13px", color: texto, lineHeight: "1.7",
              whiteSpace: "pre-wrap",
              background: oscuro ? "#091840" : "#f9f9ff",
              borderRadius: "10px", padding: "1rem",
              border: `1px solid ${borde}`
            }}>
              {vistaSimple ? instructivo.contenido_simple : instructivo.contenido_texto}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
